import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useMeta } from '../meta';
import { CustomFieldInput, ErrorBox, PriorityIcon, TypeIcon } from '../components';
import { PendingFiles, pastedFiles } from '../attachments';
import { TagInput } from '../TagInput';
import { useInvalidField } from '../formInvalid';
import { MentionTextarea, type TextFormat } from '../editor';
import type { IssueDetail, ProjectInfo } from '../types';
import { t } from '../i18n';
import { IssuePlanningFields, planningPayload } from '../IssuePlanningFields';
import { effortInput } from '../../../shared/issuePlanning';

const EMPTY = {
  story_points: '',
  estimated_time: '',
  needed_time: '',
  category_id: '',
  reproducibility: '70',
  severity: '50',
  priority: '30',
  handler_id: '',
  platform: '',
  os: '',
  os_build: '',
  version: '',
  target_version: '',
  build: '',
  summary: '',
  description: '',
  steps_to_reproduce: '',
  additional_information: '',
  view_state: '10',
  tags: '',
};

/** Jira-style "Create issue" form; used in the Create dialog and on /report. */
export function ReportForm({
  compact,
  cloneId = 0,
  onCreated,
  onCancel,
}: {
  compact?: boolean;
  cloneId?: number;
  onCreated: (id: number, another: boolean) => void;
  onCancel?: () => void;
}) {
  const { meta, projectId, setProjectId, items, th } = useMeta();
  const qc = useQueryClient();
  const reportable = meta.projects.filter((p) => p.access >= th('report_bug'));
  const [pid, setPid] = useState<number>(
    projectId && reportable.some((p) => p.id === projectId) ? projectId : reportable.length === 1 ? reportable[0].id : 0,
  );
  const [f, setF] = useState(EMPTY);
  const [textFormat, setTextFormat] = useState<TextFormat>('auto');
  const [cf, setCf] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<File[]>([]);
  const [another, setAnother] = useState(false);
  const [more, setMore] = useState(false);
  const [created, setCreated] = useState<number | null>(null);
  const [dropping, setDropping] = useState(false);

  // paste screenshots anywhere (also in the text fields) to attach them. Capture phase, and stopped there, so the
  // note form of an issue page behind the Create dialog doesn't take them too.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const pasted = pastedFiles(e.clipboardData);
      if (!pasted.length) return;
      e.preventDefault();
      e.stopPropagation();
      setFiles((cur) => [...cur, ...pasted]);
    };
    window.addEventListener('paste', onPaste, true);
    return () => window.removeEventListener('paste', onPaste, true);
  }, []);

  const clone = useQuery({ queryKey: ['issue', cloneId], queryFn: () => api.get<IssueDetail>(`/api/issues/${cloneId}`), enabled: cloneId > 0 });
  useEffect(() => {
    if (!clone.data) return;
    const i = clone.data.issue;
    setPid(i.project_id);
    setF({
      ...EMPTY,
      category_id: i.category_id == null ? '' : String(i.category_id),
      reproducibility: String(i.reproducibility),
      severity: String(i.severity),
      priority: String(i.priority),
      platform: i.platform,
      os: i.os,
      os_build: i.os_build,
      version: i.version,
      target_version: i.target_version,
      build: i.build,
      summary: i.summary,
      story_points: i.story_points ?? '',
      estimated_time: effortInput(i.estimated_minutes),
      description: i.description,
      steps_to_reproduce: i.steps_to_reproduce,
      additional_information: i.additional_information,
      view_state: String(i.view_state),
      tags: clone.data.tags.map((t) => t.name).join(', '),
    });
    setCf(Object.fromEntries(clone.data.custom_fields.map((c) => [String(c.id), c.value ?? ''])));
    setTextFormat(i.text_format ?? 'auto');
    setMore(true);
  }, [clone.data]);

  const info = useQuery({ queryKey: ['project', pid], queryFn: () => api.get<ProjectInfo>(`/api/projects/${pid}`), enabled: pid > 0 });
  const invalid = useInvalidField();
  // preselect the category "General" (Mantis' default category) when the project has it and none is chosen yet
  useEffect(() => {
    const general = info.data?.categories.find((c) => /^(general|allgemein)$/i.test(c.name.trim()));
    if (general) setF((cur) => (cur.category_id ? cur : { ...cur, category_id: String(general.id) }));
  }, [info.data]);
  const lvl = info.data?.access ?? 0;
  const set = (k: keyof typeof EMPTY) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const submit = useMutation({
    mutationFn: async () => {
      const { id } = await api.post<{ id: number }>('/api/issues', {
        project_id: pid,
        category_id: f.category_id ? Number(f.category_id) : null,
        reproducibility: Number(f.reproducibility),
        severity: Number(f.severity),
        priority: Number(f.priority),
        handler_id: f.handler_id ? Number(f.handler_id) : null,
        platform: f.platform,
        os: f.os,
        os_build: f.os_build,
        version: f.version,
        target_version: f.target_version,
        build: f.build,
        summary: f.summary,
        ...planningPayload(f, lvl >= th('time_tracking')),
        description: f.description,
        steps_to_reproduce: f.steps_to_reproduce,
        additional_information: f.additional_information,
        text_format: textFormat,
        view_state: Number(f.view_state),
        custom_fields: cf,
        tags: f.tags.split(/[,;]/).map((t) => t.trim()).filter(Boolean),
      });
      if (files.length) {
        const fd = new FormData();
        for (const file of files) fd.append('file', file, file.name);
        await api.post(`/api/issues/${id}/attachments`, fd);
      }
      if (cloneId) await api.post(`/api/issues/${id}/relationships`, { type: 1, target_id: cloneId });
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['issues'] });
      qc.invalidateQueries({ queryKey: ['myview'] });
      qc.invalidateQueries({ queryKey: ['meta'] });
      if (another) {
        setCreated(id);
        setF({ ...f, summary: '', description: '', steps_to_reproduce: '', additional_information: '' });
        setFiles([]);
      } else {
        onCreated(id, false);
      }
    },
  });

  const reportFields = (info.data?.custom_fields ?? []).filter((c) => c.display_report && lvl >= (c.access_level_rw ?? 0));
  const required = <span className="req">*</span>;

  return (
    <form
      className={`create-form ${compact ? 'compact' : ''} ${dropping ? 'drag' : ''}`}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDropping(true);
      }}
      onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setDropping(false)}
      onDrop={(e) => {
        if (!e.dataTransfer.files.length) return;
        e.preventDefault();
        setDropping(false);
        setFiles((cur) => [...cur, ...e.dataTransfer.files]);
      }}
      onInvalidCapture={invalid.onInvalidCapture}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        invalid.clear();
        submit.mutate();
      }}
    >
      {created && (
        <div className="banner success">
          <Link to={`/issues/${created}`}>#{created}</Link> {t('has been reported.')}
        </div>
      )}
      <p className="subtle small">
        {t('Required fields are marked with an asterisk')} {required}
      </p>

      <div className="field-row">
        <label className="field">
          <span className="field-label">
            {t('Project')} {required}
          </span>
          <select
            value={pid}
            onChange={(e) => {
              const id = Number(e.target.value);
              setPid(id);
              setF({ ...f, category_id: '', version: '', target_version: '', handler_id: '' });
              if (id && !compact) setProjectId(id);
            }}
            required
          >
            <option value={0}>{t('Select a project')}</option>
            {reportable.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">
            {t('Issue type')} {required}
          </span>
          <span className="with-icon">
            <TypeIcon severity={Number(f.severity)} />
            <select value={f.severity} onChange={set('severity')}>
              {items('severity').map((e) => (
                <option key={e.value} value={e.value}>
                  {e.value === 10 ? t('Feature') : e.value <= 40 ? t('Task') : t('Bug')} — {e.label}
                </option>
              ))}
            </select>
          </span>
        </label>
      </div>

      {pid > 0 && info.data && (
        <>
          <hr />
          <label className="field">
            <span className="field-label">
              {t('Summary')} {required}
            </span>
            <input value={f.summary} onChange={set('summary')} maxLength={255} required autoFocus />
          </label>
          <div className="field-row">
            <label className="field">
              <span className="field-label">
                {t('Component (category)')} {info.data.categories.length > 0 && required}
              </span>
              <select value={f.category_id} onChange={set('category_id')} required={info.data.categories.length > 0}>
                <option value="">{t('Select…')}</option>
                {info.data.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.project_id == null ? ` (${t('global')})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">{t('Priority')}</span>
              <span className="with-icon">
                <PriorityIcon priority={Number(f.priority)} />
                <select value={f.priority} onChange={set('priority')}>
                  {items('priority').map((e) => (
                    <option key={e.value} value={e.value}>
                      {e.label}
                    </option>
                  ))}
                </select>
              </span>
            </label>
          </div>
          <div className="field">
            <span className="field-label">
              {t('Description')}
            </span>
            <MentionTextarea
              rows={compact ? 6 : 8}
              value={f.description}
              onChange={(v) => setF({ ...f, description: v })}
              format={textFormat}
              onFormatChange={setTextFormat}
              placeholder={t('What happened? What did you expect?')}
            />
          </div>
          <div className="field-row">
            {lvl >= th('update_bug_assign') ? (
              <label className="field">
                <span className="field-label">{t('Assignee')}</span>
                <select value={f.handler_id} onChange={set('handler_id')}>
                  <option value="">{t('Automatic / unassigned')}</option>
                  {info.data.handlers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.realname || u.username}
                    </option>
                  ))}
                </select>
                {info.data.handlers.some((u) => u.id === meta.user.id) && (
                  <button type="button" className="link small" onClick={() => setF({ ...f, handler_id: String(meta.user.id) })}>
                    {t('Assign to me')}
                  </button>
                )}
              </label>
            ) : (
              <span />
            )}
            <label className="field">
              <span className="field-label">{t('Labels')}</span>
              <TagInput value={f.tags} onChange={(v) => setF({ ...f, tags: v })} options={meta.tags.map((tg) => tg.name)} placeholder={t('label1, label2')} />
            </label>
          </div>
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('Affects version')}</span>
              <select value={f.version} onChange={set('version')}>
                <option value="">{t('None')}</option>
                {info.data.versions
                  .filter((v) => !v.obsolete)
                  .map((v) => (
                    <option key={v.id} value={v.version}>
                      {v.version}
                    </option>
                  ))}
              </select>
            </label>
            {lvl >= th('update_bug_status') ? (
              <label className="field">
                <span className="field-label">{t('Fix version (target)')}</span>
                <select value={f.target_version} onChange={set('target_version')}>
                  <option value="">{t('None')}</option>
                  {info.data.versions
                    .filter((v) => !v.obsolete && !v.released)
                    .map((v) => (
                      <option key={v.id} value={v.version}>
                        {v.version}
                      </option>
                    ))}
                </select>
              </label>
            ) : (
              <span />
            )}
          </div>
          {reportFields.length > 0 && (
            <div className="field-row">
              {reportFields.map((c) => (
                <label key={c.id} className="field">
                  <span className="field-label">
                    {c.name} {c.require_report && required}
                  </span>
                  <CustomFieldInput field={c} value={cf[String(c.id)] ?? c.default_value ?? ''} onChange={(v) => setCf({ ...cf, [String(c.id)]: v })} />
                </label>
              ))}
            </div>
          )}
          <IssuePlanningFields value={f} onChange={(key, value) => setF({ ...f, [key]: value })} canTime={lvl >= th('time_tracking')} />
          <div className="field">
            <span className="field-label">{t('Attachments')}</span>
            <div className="attach-row">
              <label className="btn subtle file-btn">
                📎 {t('Attach files')}
                <input type="file" multiple hidden onChange={(e) => {
                  setFiles((cur) => [...cur, ...(e.target.files ?? [])]);
                  e.target.value = '';
                }} />
              </label>
              <span className="subtle small">{t('or paste a screenshot, or drop files on the form')}</span>
            </div>
            <PendingFiles files={files} onChange={setFiles} />
          </div>

          <button type="button" className="link" onClick={() => setMore(!more)}>
            {more ? `▾ ${t('Fewer fields')}` : `▸ ${t('More fields (steps, environment, visibility)')}`}
          </button>
          {more && (
            <div className="more-fields">
              <div className="field">
                <span className="field-label">{t('Steps to reproduce')}</span>
                <MentionTextarea rows={4} value={f.steps_to_reproduce} onChange={(v) => setF({ ...f, steps_to_reproduce: v })} format={textFormat} />
              </div>
              <div className="field">
                <span className="field-label">{t('Additional information')}</span>
                <MentionTextarea rows={3} value={f.additional_information} onChange={(v) => setF({ ...f, additional_information: v })} format={textFormat} />
              </div>
              <div className="field-row three">
                <label className="field">
                  <span className="field-label">{t('Reproducibility')}</span>
                  <select value={f.reproducibility} onChange={set('reproducibility')}>
                    {items('reproducibility').map((e) => (
                      <option key={e.value} value={e.value}>
                        {e.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">{t('Visibility')}</span>
                  <select value={f.view_state} onChange={set('view_state')}>
                    <option value="10">{t('Public')}</option>
                    <option value="50">{t('Private')}</option>
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">{t('Build')}</span>
                  <input value={f.build} onChange={set('build')} />
                </label>
                <label className="field">
                  <span className="field-label">{t('Platform')}</span>
                  <input value={f.platform} onChange={set('platform')} />
                </label>
                <label className="field">
                  <span className="field-label">{t('OS')}</span>
                  <input value={f.os} onChange={set('os')} />
                </label>
                <label className="field">
                  <span className="field-label">{t('OS version')}</span>
                  <input value={f.os_build} onChange={set('os_build')} />
                </label>
              </div>
            </div>
          )}
        </>
      )}

      {invalid.message && <div className="banner error form-invalid" role="alert">{invalid.message}</div>}
      <ErrorBox error={submit.error} />
      <div className="form-footer">
        <label className="check">
          <input type="checkbox" checked={another} onChange={(e) => setAnother(e.target.checked)} /> {t('Report Stay (report another issue)')}
        </label>
        <span className="spacer" />
        {onCancel && (
          <button type="button" className="btn subtle" onClick={onCancel}>
            {t('Cancel')}
          </button>
        )}
        <button className="btn primary" disabled={submit.isPending || !pid}>
          {submit.isPending ? t('Submitting…') : t('Submit Issue')}
        </button>
      </div>
    </form>
  );
}

export function ReportPage() {
  const [params] = useSearchParams();
  const cloneId = Number(params.get('clone')) || 0;
  const navigate = useNavigate();
  return (
    <div className="page narrow-page">
      <div className="page-head">
        <h1>{cloneId ? t('Report Issue (clone of {id})', { id: String(cloneId).padStart(7, '0') }) : t('Report Issue')}</h1>
      </div>
      <div className="card">
        <div className="card-body">
          <ReportForm cloneId={cloneId} onCreated={(id) => navigate(`/issues/${id}`)} onCancel={() => navigate(-1)} />
        </div>
      </div>
    </div>
  );
}
